<ng-container *ngIf="config; else loadingTemplate">
  <div class="config-form">
    <!-- Mode Selector (hidden when validity mask marks register as unsupported) -->
    <ng-container *ngIf="config.modeSelector && isModeSelectorVisible()">
      <ng-container *ngIf="config.modeSelector as modeSelector">
        <div class="mode-selector section-card">
          <div class="section-header">
            <span class="section-label body-3 text-grey-400 text-400 text-uppercase">
              {{ modeSelector.label | translate }}
            </span>
          </div>
          <div class="section-content">
            <ion-select
              class="w-full data-type-select field-control mode-select"
              interface="action-sheet"
              [disabled]="isBusy"
              [value]="currentMode"
              (ionChange)="onModeChange($any($event).detail.value)">
              <ion-select-option
                *ngFor="let option of modeSelector.options"
                [value]="option.value">
                {{ option.label | translate }}
              </ion-select-option>
            </ion-select>

            <!-- Apply button: only shown in manual mode -->
            <ng-container *ngIf="modeSelector.applyMode !== 'immediate' && currentMode !== originalValues[modeSelector.key]">
              <ion-button
                class="ion-button-accent text-white radius-4"
                size="small"
                [disabled]="isSaving"
                (click)="saveModeSelector()">
                {{ isSaving ? _labels.saving : _labels.apply }}
              </ion-button>
            </ng-container>

            <!-- Immediate mode: saving indicator -->
            <ion-spinner
              *ngIf="modeSelector.applyMode === 'immediate' && isModeApplying"
              class="mode-apply-spinner"
              name="crescent">
            </ion-spinner>
          </div>

          <!-- Mode Selector Error -->
          <div
            *ngIf="lastSaveError && lastSaveError.type === 'save-mode'"
            class="section-error"
            (click)="clearSaveError()">
            <span class="error-icon">⚠</span>
            <span class="error-message">{{ _labels.saveFailed }}</span>
            <span class="error-dismiss">✕</span>
          </div>

          <!-- Global Read: visible when no section has its own Read button -->
          <div
            *ngIf="!hasVisibleSections()"
            class="section-actions">
            <ion-button
              class="btn-read radius-4"
              size="small"
              [disabled]="isSaving || isReading"
              (click)="requestRead()">
              {{ isReading ? _labels.reading : _labels.read }}
            </ion-button>
          </div>
        </div>
      </ng-container>
    </ng-container>

    <!-- Presets -->
    <ng-container *ngIf="getAvailablePresets() as presets">
      <div
        *ngIf="presets.length > 0"
        class="presets-bar">
        <button
          *ngFor="let preset of presets"
          class="preset-card"
          type="button"
          [class.preset-applied]="appliedPresetId === preset.id"
          (click)="applyPreset(preset)">
          <ion-icon
            *ngIf="preset.icon"
            class="preset-icon"
            [name]="preset.icon">
          </ion-icon>
          <div class="preset-text">
            <span class="preset-label">{{ preset.label | translate }}</span>
            <span
              *ngIf="preset.description"
              class="preset-description">
              {{ preset.description | translate }}
            </span>
          </div>
          <ion-icon
            *ngIf="appliedPresetId === preset.id"
            class="preset-check"
            name="checkmark-circle">
          </ion-icon>
        </button>
      </div>
    </ng-container>

    <!-- Unsupported notice (all sections hidden by validity mask) -->
    <section *ngIf="!hasVisibleContent" class="border-1 fx-row fx-align-content-start fx-gap-3 p-1 radius-4 border-accent bg-accent-100 w-full">
      <ion-icon name="information-circle-outline" class="text-accent s-6 min-s-6"></ion-icon>
      <p class="fx-g-1 tw-text-sm tw-font-normal text-normal-wrap" style="color: var(--accent)">{{ _labels.unsupported }}</p>
    </section>

    <!-- Sections -->
    <ng-container *ngFor="let section of config.sections">
      <div
        *ngIf="isSectionVisible(section)"
        class="section-card"
        [class.dirty]="isSectionDirty(section)"
        [class.section-blocked]="isSectionBusy(section)">
        <div
          *ngIf="!isSingleSection"
          class="section-header">
          <span class="section-label body-3 text-grey-400 text-400 text-uppercase">
            {{ section.label | translate }}
          </span>
          <span
            *ngIf="isSectionDirty(section)"
            class="dirty-indicator">
            *
          </span>
        </div>

        <!-- Section Selector -->
        <ng-container *ngIf="section.sectionSelector as sel">
          <div class="section-selector">
            <label class="field-label body-3 text-grey-400 text-400">{{ sel.label | translate }}</label>
            <div class="section-selector-control">
              <ion-select
                class="field-control w-full data-type-select"
                interface="action-sheet"
                [disabled]="isSectionBusy(section)"
                [value]="formValues[sel.key]"
                (ionChange)="onSectionSelectorChange(section, $any($event).detail.value)">
                <ion-select-option
                  *ngFor="let opt of sel.options"
                  [value]="opt.value">
                  {{ opt.label | translate:{ value: opt.value } }}
                </ion-select-option>
              </ion-select>
              <ion-spinner
                *ngIf="sel.applyMode === 'immediate' && isSectionApplying(section)"
                class="section-apply-spinner"
                name="crescent">
              </ion-spinner>
            </div>
          </div>
        </ng-container>

        <!-- Custom Section Content -->
        <ng-container *ngIf="section.customContent && customSectionTemplates?.[section.customContent]; else standardSectionContent">
          <ng-container
            *ngTemplateOutlet="$any(customSectionTemplates)[section.customContent];
              context: { $implicit: getCustomSectionContext(section) }">
          </ng-container>
        </ng-container>

        <ng-template #standardSectionContent>
          <div class="section-fields">
            <ng-container *ngFor="let field of section.fields">

              <!-- ═══ Bit Toggle: rendered as sub-group header ═══ -->
              <ng-container *ngIf="field.type === 'bit-toggle' && isFieldVisible(field)">
                <div
                  class="bit-toggle-header"
                  [class.bit-toggle-on]="!!formValues[field.key]"
                  [class.bit-toggle-off]="!formValues[field.key]">
                  <div class="bit-toggle-label">
                    <span class="body-3 text-400">{{ field.label | translate }}</span>
                    <span class="bit-toggle-badge" [class.badge-on]="!!formValues[field.key]">
                      {{ (formValues[field.key] ? 'i18n.Common.enabled' : 'i18n.Common.disabled') | translate }}
                    </span>
                  </div>
                  <ion-toggle
                    [checked]="!!formValues[field.key]"
                    (ionChange)="onFieldChange(field, $any($event).detail.checked)">
                  </ion-toggle>
                </div>
              </ng-container>

              <!-- ═══ Bit Select: rendered as sub-group header ═══ -->
              <ng-container *ngIf="field.type === 'bit-select' && isFieldVisible(field)">
                <div class="bit-select-header">
                  <label class="field-label body-3 text-grey-400 text-400">
                    {{ field.label | translate }}
                  </label>
                  <div class="field-input">
                    <ion-select
                      class="field-control w-full data-type-select"
                      interface="action-sheet"
                      [value]="formValues[field.key]"
                      (ionChange)="onFieldChange(field, $any($event).detail.value)">
                      <ion-select-option
                        *ngFor="let opt of getFieldOptions(field)"
                        [value]="opt.value">
                        {{ opt.label | translate }}
                      </ion-select-option>
                    </ion-select>
                  </div>
                </div>
              </ng-container>

              <!-- ═══ Checkbox (binary enable/disable — rendered as toggle) ═══ -->
              <ng-container *ngIf="field.type === 'checkbox' && isFieldVisible(field)">
                <div
                  class="bit-toggle-header"
                  [class.bit-toggle-on]="!!formValues[field.key]"
                  [class.bit-toggle-off]="!formValues[field.key]"
                  [class.dependent-field]="field.visibleWhen">
                  <div class="bit-toggle-label">
                    <span class="body-3 text-400">{{ field.label | translate }}</span>
                    <span class="bit-toggle-badge" [class.badge-on]="!!formValues[field.key]">
                      {{ (formValues[field.key] ? 'i18n.Common.enabled' : 'i18n.Common.disabled') | translate }}
                    </span>
                  </div>
                  <ion-toggle
                    [checked]="!!formValues[field.key]"
                    (ionChange)="onFieldChange(field, $any($event).detail.checked ? 1 : 0)">
                  </ion-toggle>
                </div>
              </ng-container>

              <!-- ═══ Standard fields (non-bit, non-checkbox) ═══ -->
              <ng-container *ngIf="field.type !== 'bit-toggle' && field.type !== 'bit-select' && field.type !== 'checkbox'">
              <div
                *ngIf="isFieldVisible(field)"
                class="field-row"
                [class.has-error]="errors[field.key]"
                [class.dependent-field]="field.visibleWhen">
                <label
                  *ngIf="field.type !== 'trigger' || !field.constraint?.label"
                  class="field-label body-3 text-grey-400 text-400">
                  {{ field.label | translate }}
                </label>

                <div
                  class="field-input"
                  [class.composite]="field.type === 'time' || field.type === 'date' || field.type === 'weekday'"
                  [class.trigger-field]="field.type === 'trigger'">
                  <ng-container [ngSwitch]="field.type">
                    <!-- Input -->
                    <ng-container *ngSwitchCase="'input'">
                      <ion-input
                        class="field-control w-full"
                        [max]="field.constraint?.max"
                        [min]="field.constraint?.min"
                        [step]="field.constraint?.step || 1"
                        [type]="field.inputType || 'number'"
                        [value]="formValues[field.key]"
                        (ionChange)="onFieldChange(field, $any($event).detail.value)">
                      </ion-input>
                    </ng-container>

                    <!-- Select -->
                    <ng-container *ngSwitchCase="'select'">
                      <ion-select
                        class="field-control w-full data-type-select"
                        interface="action-sheet"
                        [value]="formValues[field.key]"
                        (ionChange)="onFieldChange(field, $any($event).detail.value)">
                        <ion-select-option
                          *ngFor="let opt of getFieldOptions(field)"
                          [value]="opt.value">
                          {{ opt.label | translate }}
                        </ion-select-option>
                      </ion-select>
                    </ng-container>

                    <!-- Time (HH:MM) -->
                    <ng-container *ngSwitchCase="'time'">
                      <div class="date-select-group">
                        <ion-select
                          class="date-select hour-select data-type-select"
                          interface="action-sheet"
                          [attr.aria-label]="field.label + ' Hour'"
                          [title]="field.label + ' Hour'"
                          [value]="getTimeHour(field)"
                          (ionChange)="setTimeHour(field, $any($event).detail.value)">
                          <ion-select-option
                            *ngFor="let h of hours"
                            [value]="h">
                            {{ h < 10 ? '0' + h : h }}
                          </ion-select-option>
                        </ion-select>
                        <span class="date-separator">:</span>
                        <ion-select
                          class="date-select minute-select data-type-select"
                          interface="action-sheet"
                          [attr.aria-label]="field.label + ' Minute'"
                          [title]="field.label + ' Minute'"
                          [value]="getTimeMinute(field)"
                          (ionChange)="setTimeMinute(field, $any($event).detail.value)">
                          <ion-select-option
                            *ngFor="let m of minutes"
                            [value]="m">
                            {{ m < 10 ? '0' + m : m }}
                          </ion-select-option>
                        </ion-select>
                      </div>
                    </ng-container>

                    <!-- Date (MM/DD) -->
                    <ng-container *ngSwitchCase="'date'">
                      <div class="date-select-group">
                        <ion-select
                          class="date-select month-select"
                          interface="action-sheet"
                          [attr.aria-label]="field.label + ' Month'"
                          [title]="'Month'"
                          [value]="getDateMonth(field)"
                          (ionChange)="setDateMonth(field, $any($event).detail.value)">
                          <ion-select-option
                            *ngFor="let m of months"
                            [value]="m.value">
                            {{ m.label }}
                          </ion-select-option>
                        </ion-select>
                        <span class="date-separator">/</span>
                        <ion-select
                          class="date-select day-select"
                          interface="action-sheet"
                          [attr.aria-label]="field.label + ' Day'"
                          [title]="'Day'"
                          [value]="getDateDay(field)"
                          (ionChange)="setDateDay(field, $any($event).detail.value)">
                          <ion-select-option
                            *ngFor="let d of days"
                            [value]="d">
                            {{ d }}
                          </ion-select-option>
                        </ion-select>
                      </div>
                    </ng-container>

                    <!-- Weekday -->
                    <ng-container *ngSwitchCase="'weekday'">
                      <div class="weekday-selector">
                        <label
                          *ngFor="let day of weekdays"
                          class="weekday-option"
                          [class.selected]="getWeekdayValue(field).includes(day)">
                          <input
                            type="checkbox"
                            [checked]="getWeekdayValue(field).includes(day)"
                            (change)="toggleWeekday(field, day, $any($event).target.checked)">
                          <span>{{ day }}</span>
                        </label>
                      </div>
                    </ng-container>

                    <!-- Trigger -->
                    <ng-container *ngSwitchCase="'trigger'">
                      <div class="trigger-action tw-min-h-11">
                        <span
                          *ngIf="field.constraint?.label"
                          class="trigger-description body-3 text-grey-400">
                          {{ field.constraint!.label! | translate }}
                        </span>
                        <ion-button
                          class="ion-button-accent text-white radius-4 trigger-button"
                          size="small"
                          [disabled]="isSaving"
                          (click)="triggerAction(field, section)">
                          {{ _labels.trigger }}
                        </ion-button>
                      </div>
                    </ng-container>
                  </ng-container>

                  <span
                    *ngIf="field.unit"
                    class="field-unit">
                    {{ field.unit }}
                  </span>
                </div>

                <div
                  *ngIf="errors[field.key]"
                  class="field-error">
                  {{ errors[field.key] }}
                </div>
              </div>
              </ng-container>
            </ng-container>
          </div>

          <!-- Section Error -->
          <div
            *ngIf="lastSaveError && lastSaveError.sectionId === section.id"
            class="section-error"
            (click)="clearSaveError()">
            <span class="error-icon">⚠</span>
            <span class="error-message">{{ _labels.saveFailed }}</span>
            <span class="error-dismiss">✕</span>
          </div>

          <!-- Section Actions -->
          <div class="section-actions">
            <ion-button
              class="btn-read radius-4"
              size="small"
              [disabled]="isSaving || isReading"
              (click)="requestRead()">
              {{ isReading ? _labels.reading : _labels.read }}
            </ion-button>
            <div class="section-actions-end">
              <ion-button
                class="radius-4"
                fill="clear"
                size="small"
                [disabled]="!isSectionDirty(section) || isSaving || isReading"
                (click)="resetSection(section)">
                {{ _labels.discard }}
              </ion-button>
              <ion-button
                class="ion-button-accent text-white radius-4"
                size="small"
                [disabled]="!isSectionDirty(section) || !isValid || isSaving || isReading"
                (click)="saveSection(section)">
                {{ isSaving ? _labels.saving : (willSaveModeWithSection(section) ? _labels.saveAll : _labels.save) }}
              </ion-button>
            </div>
          </div>
        </ng-template>
      </div>
    </ng-container>
  </div>
</ng-container>

<ng-template #loadingTemplate>
  <div class="loading">
    <ion-spinner></ion-spinner>
  </div>
</ng-template>
